import type {
	API_CreateCustomerRequest,
	Gender,
	IdType,
} from "@pilatestime/shared-types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCustomer } from "@/api/customers";
import { PageHeader } from "@/components/PageHeader";
import { CustomerRegistration } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerRegistration";
import { validateCPF } from "@/utils/cpfValidation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/new",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context }) => {
		const token = await context.get_token();
		if (!token) {
			throw new Error("No token available");
		}
		return {
			token,
		};
	},
	component: NewCustomerPage,
});

function NewCustomerPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Form state
	const [formData, setFormData] = useState<API_CreateCustomerRequest>({
		firstName: "",
		lastName: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Create customer mutation
	const createCustomerMutation = useCreateCustomer();

	// Handle form field changes
	const handleFieldChange = useCallback(
		(
			field: keyof API_CreateCustomerRequest | "status",
			value: string | Gender | IdType | undefined,
		) => {
			// Skip status field for create mode
			if (field === "status") return;

			// Convert empty strings to undefined for optional fields
			let processedValue = value;
			if (typeof value === "string" && value.trim() === "") {
				processedValue = undefined;
			}

			setFormData((prev) => ({
				...prev,
				[field]: processedValue,
			}));

			// Clear error for this field
			setErrors((prev) => {
				if (prev[field]) {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				}
				return prev;
			});
		},
		[],
	);

	// Form validation
	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName?.trim()) {
			newErrors.firstName = t("customers.validation.firstNameRequired");
		}

		if (!formData.lastName?.trim()) {
			newErrors.lastName = t("customers.validation.lastNameRequired");
		}

		if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = t("customers.validation.emailInvalid");
		}

		if (formData.dateOfBirth) {
			const birthDate = new Date(formData.dateOfBirth);
			const today = new Date();
			if (birthDate > today) {
				newErrors.dateOfBirth = t("customers.validation.dateOfBirthInvalid");
			}
		}

		// CPF validation when idType is CPF
		if (formData.idType === "CPF" && formData.idNumber) {
			const cpfError = validateCPF(formData.idNumber, t);
			if (cpfError) {
				newErrors.idNumber = cpfError;
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			// Show validation error - this is not handled by mutation
			return;
		}

		try {
			// Clean up form data - remove empty strings for optional fields
			const cleanedData = { ...formData };
			for (const [key, value] of Object.entries(cleanedData)) {
				if (typeof value === "string" && value.trim() === "") {
					delete cleanedData[key as keyof API_CreateCustomerRequest];
				}
			}

			const newCustomer = await createCustomerMutation.mutateAsync({
				token,
				organizationId: orgId,
				data: cleanedData,
			});

			// Navigate to the newly created customer's details page
			navigate({
				to: "/app/org/$orgId/customers/$customerId",
				params: { orgId, customerId: newCustomer.id },
			});
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Error creating customer:", error);
		}
	};

	return (
		<div className="h-full flex flex-col space-y-6">
			{/* Header */}
			<PageHeader
				title={t("customers.addCustomer")}
				description={t("customers.addCustomerDescription")}
				showBackButton={true}
				backButtonText={t("common.back")}
			/>

			{/* Form */}
			<div className="h-full overflow-auto">
				<CustomerRegistration
					formData={formData}
					errors={errors}
					onFieldChange={handleFieldChange}
					onSubmit={handleSubmit}
					onCancel={() => window.history.back()}
					isSubmitting={createCustomerMutation.isPending}
					mode="create"
				/>
			</div>
		</div>
	);
}
